import React, { useMemo, useState } from 'react';
import moment from 'moment';

import { useSelector } from 'react-redux';
import { Tooltip } from 'antd';

import { ILuxuryHomeDetailsRateWeek } from '@share/common-types';
import { RootState } from '@share/utils';

import './style.scss';

const MAX_SHOW_MORE = 5;

// Function to get week days based on week number
const getWeekDaysFromWeekNumber = (weekNumber: number, year: number = moment().year()): string => {
  // Get the first day of the year
  const firstDayOfYear = moment().year(year).startOf('year');
  
  // Get the first day of the specified week
  let firstDayOfWeek = moment(firstDayOfYear).week(weekNumber).startOf('week');
  
  // Get the last day of the specified week
  let lastDayOfWeek = moment(firstDayOfYear).week(weekNumber).endOf('week');
  
  // Check if the week has already passed
  const now = moment();
  if (lastDayOfWeek.isBefore(now)) {
    // Move to next year
    const nextYear = year + 1;
    const nextYearFirstDay = moment().year(nextYear).startOf('year');
    
    firstDayOfWeek = moment(nextYearFirstDay).week(weekNumber).startOf('week');
    lastDayOfWeek = moment(nextYearFirstDay).week(weekNumber).endOf('week');
  }
  
  // Format the date range
  const startDate = firstDayOfWeek.format('D MMM');
  const endDate = lastDayOfWeek.format('D MMM, YYYY');
  
  return `From ${startDate} to ${endDate}`;
};

export const LuxuryHomesDetailsSeasonalPricing: React.FC = () => {

  const { loading, rates } = useSelector((state: RootState) => state.luxuryHomesDetailsStore);

  const [showMore, setShowMore] = useState(false);
  
  const weeks = rates?.weeks;
  
  const groupedWeeksByAmount = useMemo(() => {
    if (!weeks?.length) return {};

    // Filter out weeks with 0 amount
    const filteredWeeks = weeks.filter((week: ILuxuryHomeDetailsRateWeek) => (week.amount || 0) > 0);

    if (!filteredWeeks.length) return {};

    // Sort weeks by biggest amount first
    const sortedWeeks = [...filteredWeeks].sort((a, b) => (a.amount || 0) - (b.amount || 0));

    // Group weeks by amount
    const grouped = sortedWeeks.reduce((acc: { [key: string]: ILuxuryHomeDetailsRateWeek[] }, week: ILuxuryHomeDetailsRateWeek) => {
      const amount = week.amount || 0;
      const amountKey = amount.toString();
      
      if (!acc[amountKey]) {
        acc[amountKey] = [];
      }
      
      // Get week number using moment
      const weekNumber = moment(week.from, 'YYYY-MM-DD').week();
      
      // Add week number to the week object
      const weekWithNumber = {
        ...week,
        weekNumber
      };
      
      acc[amountKey].push(weekWithNumber);
      return acc;
    }, {});

    return grouped;
  }, [weeks]);

  const groupedWeeksByAmountKeysOriginal = Object.keys(groupedWeeksByAmount).sort((a, b) => Number(a) - Number(b));
  const groupedWeeksByAmountKeys = groupedWeeksByAmountKeysOriginal.slice(0, showMore ? undefined : MAX_SHOW_MORE);

  if (loading || !groupedWeeksByAmountKeysOriginal?.length) {
    return null;
  }

  return (  
    <div className="pricing-table">
      <h3>Seasonal Pricing</h3>
      <table>
        <thead>
          <tr>
            <th>Season</th>
            <th>Week(s)</th>
            <th>Price Range</th>
          </tr>
        </thead>
        <tbody>
          {groupedWeeksByAmountKeys?.map((amountKey, index) => {
            const weeksInGroup = groupedWeeksByAmount[amountKey];
            const weekNumbers = weeksInGroup.map((week: any) => week.weekNumber).sort((a: number, b: number) => a - b);
            
            return (
              <tr key={index}>
                <td><label>Season {(index + 1)}</label></td>
                <td><label style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>{weekNumbers.map((wn: number, index: number) => {
                  const weekDays = getWeekDaysFromWeekNumber(wn);
                  return (
                    <>
                      <Tooltip title={`${weekDays}`}>
                        <label key={wn}>{wn}<label style={{ marginRight: '5px' }}>{index !== weekNumbers.length - 1 && ', '}</label></label>
                      </Tooltip>
                    </>);
                })}</label></td>
                <td><label>$ {Number(amountKey).toFixed(2)}</label></td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} style={{ textAlign: 'center', fontSize: '12px', borderColor: 'white' }}>
              *Price does not include taxes and fees
            </td>
          </tr>
        </tfoot>
      </table>

      {groupedWeeksByAmountKeysOriginal.length > MAX_SHOW_MORE && (
        <div className="show-more-button">
          <div onClick={() => setShowMore(!showMore)}>
            {showMore ? 'Show Less' : 'Show More'}
          </div>
        </div>)}
    </div>
  );
};
